import { useEffect } from "react";
import { useOrganization } from "@/hooks/useOrganization";

function hexToHSL(hex: string): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  // UPPDRAG-30: l never reassigned (but h and s are, per the if-branch below).
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function needsLightForeground(hex: string): boolean {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return true;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

export default function OrgThemeInjector(): null {
  const { organization } = useOrganization();

  useEffect(() => {
    const root = document.documentElement;

    if (organization?.primary_color) {
      const hsl = hexToHSL(organization.primary_color);
      if (hsl) {
        root.style.setProperty("--primary", hsl);
        root.style.setProperty("--primary-foreground",
          needsLightForeground(organization.primary_color) ? "0 0% 100%" : "0 0% 0%"
        );
        root.style.setProperty("--sidebar-gradient-color", `hsl(${hsl} / 0.12)`);
      }
    }

    if (organization?.secondary_color) {
      const hsl = hexToHSL(organization.secondary_color);
      if (hsl) {
        root.style.setProperty("--accent", hsl);
        root.style.setProperty("--accent-foreground",
          needsLightForeground(organization.secondary_color) ? "0 0% 100%" : "0 0% 0%"
        );
      }
    }

    return () => {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--primary-foreground");
      root.style.removeProperty("--accent");
      root.style.removeProperty("--accent-foreground");
      root.style.removeProperty("--sidebar-gradient-color");
    };
  }, [organization?.primary_color, organization?.secondary_color]);

  return null;
}
